/**
 * Aba ativa (v2.1, p.21, R05): "o filete laranja desliza até a aba escolhida",
 * em 200 ms (`--mov-padrao`).
 *
 * O filete é UM só, desenhado pela barra (`filete-das-abas`, em
 * `estilos/global.css`); esta função mede a aba ativa — `aria-selected="true"`
 * ou `aria-current="page"` — e escreve a posição dela na barra. A barra quebra
 * em duas linhas quando falta largura (`barra-de-abas`), então a medida leva o
 * topo também.
 *
 * Uso: `<div role="tablist" ref={fileteDasAbas} className="barra-de-abas
 * filete-das-abas">`. É um `ref` de função (React 19 aceita a limpeza de
 * volta), e não um gancho: a barra pode nascer depois do componente — o
 * painel abre e só então desenha as abas — e o `ref` acompanha isso sozinho.
 *
 * A primeira medida chega sem transição (`data-filete="parado"`): o filete
 * nasce embaixo da aba ativa, e não desliza da borda até ela. Só a partir do
 * quadro seguinte ele desliza (`"desliza"`). Sem a medida, cada aba desenha o
 * próprio sublinhado, como antes.
 */
export function fileteDasAbas(barra: HTMLElement | null): (() => void) | undefined {
  if (!barra) return undefined;
  let quadro = 0;
  /* Recomeça parado: o React pode desligar e religar o mesmo `ref` (modo
     estrito), e o quadro que ligaria o deslizar foi cancelado na limpeza. */
  delete barra.dataset['filete'];

  const medir = () => {
    const ativa = barra.querySelector<HTMLElement>('[role="tab"][aria-selected="true"], [aria-current="page"]');
    if (!ativa) {
      delete barra.dataset['filete'];
      return;
    }
    const caixa = barra.getBoundingClientRect();
    const aba = ativa.getBoundingClientRect();
    barra.style.setProperty('--filete-x', `${String(aba.left - caixa.left)}px`);
    barra.style.setProperty('--filete-y', `${String(aba.bottom - caixa.top - 2)}px`);
    barra.style.setProperty('--filete-largura', `${String(aba.width)}px`);
    if (!barra.dataset['filete']) {
      barra.dataset['filete'] = 'parado';
      quadro = requestAnimationFrame(() => {
        barra.dataset['filete'] = 'desliza';
      });
    }
  };

  medir();
  const mudancas = new MutationObserver(medir);
  mudancas.observe(barra, { subtree: true, childList: true, attributes: true, attributeFilter: ['aria-selected', 'aria-current'] });
  const tamanho = new ResizeObserver(medir);
  tamanho.observe(barra);
  return () => {
    cancelAnimationFrame(quadro);
    mudancas.disconnect();
    tamanho.disconnect();
  };
}
